import { normalizePhoneNumber } from "../normalizePhone";
import { normalizeRowKeys } from "../case";

const toIsoCreatedDate = (value: unknown): string | undefined => {
  if (value === null || value === undefined || value === "") return undefined;

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString();
  }

  const dateText = String(value).trim();
  if (!dateText) return undefined;

  const match = dateText.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?\s*(AM|PM)?)?$/i,
  );

  if (match) {
    const month = Number(match[1]);
    const day = Number(match[2]);
    const year = Number(match[3]);
    let hour = Number(match[4] || 0);
    const minute = Number(match[5] || 0);
    const second = Number(match[6] || 0);
    const meridiem = match[7]?.toUpperCase();

    if (meridiem) {
      if (hour < 1 || hour > 12) return undefined;
      hour = hour % 12 + (meridiem === "PM" ? 12 : 0);
    }

    const isValidTime =
      hour >= 0 &&
      hour <= 23 &&
      minute >= 0 &&
      minute <= 59 &&
      second >= 0 &&
      second <= 59;
    const parsedDate = new Date(
      Date.UTC(year, month - 1, day, hour, minute, second),
    );
    const isValidDate =
      month >= 1 &&
      month <= 12 &&
      day >= 1 &&
      parsedDate.getUTCFullYear() === year &&
      parsedDate.getUTCMonth() === month - 1 &&
      parsedDate.getUTCDate() === day;

    if (isValidDate && isValidTime) return parsedDate.toISOString();
    return undefined;
  }

  if (/^\d{4}-\d{2}-\d{2}T/.test(dateText)) {
    const parsedDate = new Date(dateText);
    if (!Number.isNaN(parsedDate.getTime())) return parsedDate.toISOString();
  }

  return undefined;
};

export const transformRow = (row: Record<string, any>) => {
  const source = normalizeRowKeys(row);
  const formattedRow: Record<string, any> = {};

  const normalizeKey = (key: string) => key.replace(/\s+/g, "").toLowerCase();

  // ✅ First, check original row for created_date/created_time (before normalization)
  const originalKeys = Object.keys(row);
  for (const originalKey of originalKeys) {
    const normalizedOriginalKey = normalizeKey(originalKey);
    if (
      ["eventdate", "created_time", "createddate", "created_date"].includes(
        normalizedOriginalKey
      )
    ) {
      const value = row[originalKey];
      if (value !== undefined && value !== null && value !== "") {
        formattedRow.createdDate = value;
        break; // Found it, stop searching
      }
    }
  }

  Object.keys(source).forEach((rawKey) => {
    const value = source[rawKey];
    if (value === undefined || value === "") return;

    const normalize = normalizeKey(rawKey);

    // Skip if we already found createdDate from original row
    if (formattedRow.createdDate && normalize === "createddate") {
      return;
    }

    // Full name / Name -> name
    if (["fullname", "name", "full_name"].includes(normalize)) {
      formattedRow.name = value;
      return;
    }

    // created_time / created_date -> createdDate
    if (
      ["eventdate", "created_time", "createddate", "created_date"].includes(
        normalize
      )
    ) {
      formattedRow.createdDate = value;
      return;
    }

    // Phone Number -> phoneNumber
    if (
      [
        "phone_number",
        "phoneNumber",
        "contactno",
        "phone",
        "phonenumber",
      ].includes(normalize)
    ) {
      const { phoneNumber, countryCode, country } = normalizePhoneNumber(value);
      formattedRow.phoneNumber = phoneNumber;
      formattedRow.countryCode = countryCode;
      formattedRow.country = country;
      return;
    }

    // H.Qualification -> qualification
    if (
      ["h.qualification", "hqualification", "qualification"].includes(normalize)
    ) {
      const val = String(value).toLowerCase().trim();
      if (val === "spm") formattedRow.qualification = "SPM";
      else if (val === "高三") formattedRow.qualification = "UEC";
      else if (val === "uec" || val === "stpm_uec")
        formattedRow.qualification = "UEC";
      else formattedRow.qualification = "Others";
      return;
    }

    // State -> location
    if (normalize === "state") {
      formattedRow.location = value;
      return;
    }

    // Platform -> leadSource
    if (normalize === "platform") {
      const val = String(value).toLowerCase().trim();
      formattedRow.leadSource =
        val === "fb" || val === "facebook" ? "Online" : value;
      return;
    }

    // campaign_name -> campaign
    if (["campaign_name", "campaignname"].includes(normalize)) {
      formattedRow.campaign = value;
      return;
    }

    // counselor/consultant -> assignee
    if (normalize === "counselor" || normalize === "consultant") {
      const val = String(value).toLowerCase().trim();
      if (val === "yf" || val === "you fang") {
        formattedRow.assignee = "You Fang";
      } else if (val === "anderlyn" || val === "ad") {
        formattedRow.assignee = "Anderlyn";
      } else if (val === "matthew" || val === "m") {
        formattedRow.assignee = "Matthew";
      } else if (val === "elmo" || val === "el") {
        formattedRow.assignee = "Elmo";
      } else if (val === "su xin" || val === "sx") {
        formattedRow.assignee = "Su Xin";
      } else if (val === "nurain" || val === "ns") {
        formattedRow.assignee = "Nurain Shahira";
      } else {
        formattedRow.assignee = value; // fallback: keep original
      }
      return;
    }

    // Copy only allowed canonical Firestore fields
    const allowed = new Set([
      "ic",
      "name",
      "country",
      "countryCode",
      "phoneNumber",
      "email",
      "status",
      "assignee",
      "assigneeId",
      "leadSource",
      "campaign",
      "createdDate",
      "firstTouchedDate",
      "lastTouchedDate",
      "location",
      "preferredUniversity",
      "intake",
      "campusVisit",
      "videoCall",
      "familyFinancials",
      "studentResult",
      "estimatedResult",
      "overseas",
      "interestedCourses",
      "qualification",
      "schoolName",
      "reason",
      "remarks",
    ]);

    if (allowed.has(rawKey)) {
      formattedRow[rawKey] = value;
    }
  });

  // ✅ Normalize qualification AFTER mapping
  if (formattedRow.qualification) {
    const q = String(formattedRow.qualification).toLowerCase().trim();
    if (q === "spm") formattedRow.qualification = "SPM";
    else if (q === "uec") formattedRow.qualification = "UEC";
    else formattedRow.qualification = "Others";
  }

  // ✅ Normalize IC: keep only digits (remove dashes, spaces, symbols)
  if (formattedRow.ic) {
    const normalizedIc = String(formattedRow.ic).replace(/\D+/g, "");
    formattedRow.ic = normalizedIc;
  }

  // ✅ Convert createdDate if present
  if (
    formattedRow.createdDate !== undefined &&
    formattedRow.createdDate !== null &&
    formattedRow.createdDate !== ""
  ) {
    const originalValue = formattedRow.createdDate;

    const isoCreatedDate = toIsoCreatedDate(originalValue);

    if (isoCreatedDate) {
      formattedRow.createdDate = isoCreatedDate;
    } else {
      console.warn(
        "Failed to convert createdDate to ISO 8601. Original value:",
        originalValue,
        "Type:",
        typeof originalValue,
      );
      formattedRow.createdDate = originalValue;
    }
  }

  return formattedRow;
};

// ✅ Enhanced Duplicate Logic
export const getDeduplicationKey = (row: Record<string, any>) => {
  const source = normalizeRowKeys(row);

  // ✅ IC dedupe first
  const icDigits =
    source.ic !== undefined && source.ic !== null
      ? String(source.ic).replace(/\D+/g, "")
      : "";
  if (icDigits) return `ic:${icDigits}`;

  // ✅ If IC is blank → use phone ONLY
  const phone = source.phoneNumber
    ?.replace(/\D+/g, "") // remove non-digits (spaces, -, etc.)
    .trim()
    .toLowerCase();

  if (phone) return `phone:${phone}`;

  return "";
};

export const isDuplicate = (
  row: Record<string, any>,
  existingDocs: Map<string, any>
) => {
  const key = getDeduplicationKey(row);
  if (!key) return false; // No IC and missing one of name/phone → allow import
  return existingDocs.has(key);
};
